import { useNotifications as useNovuNotifications } from '@novu/react'
import type { UseNotificationsProps } from '@novu/react'
import { useMemo } from 'react'

interface UseNotificationsOptions {
  archived?: boolean
  read?: boolean | null
  tags?: string[]
  limit?: number
}

/**
 * Custom hook to fetch and manage Novu notifications
 * Wraps the @novu/react useNotifications hook with our app's logic
 */
export function useNotifications(options: UseNotificationsOptions = {}) {
  const { archived = false, read, tags, limit = 10 } = options

  // Build filter for Novu
  const filter: UseNotificationsProps = useMemo(() => {
    const f: UseNotificationsProps = {
      archived,
      limit,
    }

    // Only add read filter if explicitly set (not null)
    if (read !== null && read !== undefined) {
      f.read = read
    }

    if (tags && tags.length > 0) {
      f.tags = tags
    }

    return f
  }, [archived, read, tags, limit])

  // Use Novu's useNotifications hook
  const {
    notifications,
    hasMore,
    isLoading,
    isFetching,
    error,
    fetchMore,
    refetch,
    readAll,
    archiveAll,
    archiveAllRead,
  } = useNovuNotifications(filter)

  return {
    notifications: notifications || [],
    hasMore: hasMore || false,
    isLoading,
    isFetching,
    error,
    fetchMore,
    refetch,
    readAll,
    archiveAll,
    archiveAllRead,
  }
}
